import './MessageList.scss'
import {createRef, Fragment, useContext, useEffect, useState} from "react";
import AppContext from "../../context/AppContext";
import {useTranslation} from "react-i18next";
import UserBadge from "../general/UserBadge";
import {ChatMessage} from "../../context/app/useChat";

const MessageField = (props: { message: ChatMessage }) => {
  const {t} = useTranslation()
  const { markRead } = useContext(AppContext)

  useEffect(() => {
    if (!props.message.read) {
      markRead(props.message.id)
    }
  }, []);

  return (
    <>
      <div className="body">
        { props.message.decrypted ? (
          // RISKS.md/P1.31: typed by a participant, so never render it as HTML.
          <span>{props.message.decrypted}</span>
        ) : props.message.unreadable ? (
          <span className="unreadable">{t('chat.notReadableHere')}</span>
        ) : (
          <i className="fa fa-lock"></i>
        )}
      </div>
    </>
  )
}

const MessageFrom = ({uuid, fromName}: { uuid: string, fromName?: string }) => {
  const {t} = useTranslation()

  const {
    users,
    userData,
    userNames,
  } = useContext(AppContext)

  // Sender may be gone from `users` (e.g. a moderator-removed participant whose chat history
  // remains) — guard so a stale message doesn't throw on `.name`/`.uuid` and blank the room, and
  // fall back to the persisted name cache so we still show who wrote it.
  const user = users.find(u => u.uuid === uuid)

  return (
    <div className="from">
      <span className="username">
        {user && user.uuid === userData?.uuid ? t('you') : (user?.name || userNames[uuid] || fromName || '')}
      </span>
    </div>
  )
}

export default () => {
  const {t} = useTranslation()
  const listRef = createRef<HTMLDivElement>()

  const {
    userData,
    users,
    messages,
    userNames,
  } = useContext(AppContext)

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight
    }
  }, [ messages ]);

  const getBadge = (message: ChatMessage) => {
    // Same guard as MessageFrom: an unguarded `.name` on a removed sender's message crashed the
    // entire meeting-room render (white screen) while the chat sidebar was open. Fall back to the
    // persisted name cache (then empty) instead of throwing.
    const user = users.find(u => u.uuid === message.from)
    return <UserBadge name={user?.name || userNames[message.from] || message.fromName || ''}/>
  }

  const getClassnames = (message: ChatMessage) => {
    const classnames: string[] = []

    if (message.from === userData?.uuid) {
      classnames.push('outgoing')
    } else {
      classnames.push('incoming')
    }

    return classnames.join(' ')
  }

  // Below the unreadable rows, not atop the list: the list opens scrolled to the bottom.
  const lastUnreadable = messages.map(m => !!m.unreadable).lastIndexOf(true)

  return (
    <div className="message-list" ref={listRef}>
      <ul className="messages">
        {messages.map((message, i) => (
          <Fragment key={message.id}>
            <li className={getClassnames(message)}>
              <div className="inner">
                <div className="badge">
                  {getBadge(message)}
                </div>
                <div className="content">
                  <header>
                    <MessageFrom uuid={message.from} fromName={message.fromName} />
                  </header>
                  <MessageField message={message}></MessageField>
                </div>
              </div>
            </li>
            {i === lastUnreadable ? (
              <li className="unreadable-note">{t('chat.notReadableHereNote')}</li>
            ) : null}
          </Fragment>
        ))}
      </ul>
    </div>
  )
}